import { BaseApiService } from './BaseApiService';
import {
  ReportFilters,
  ExecutiveDashboardData,
  TopDecliner,
  YearEndProjection,
  ItemMode,
  SegmentedDashboardData,
  DetailedItemData,
} from '../types/Report';

function appendReportFilterIdsToParams(
  params: Record<string, unknown>,
  filters: ReportFilters
): void {
  if (filters.productIds && filters.productIds.length > 0) {
    params.productIds = filters.productIds.join(',');
  }
  if (filters.categoryIds && filters.categoryIds.length > 0) {
    params.categoryIds = filters.categoryIds.join(',');
  }
  if (filters.brandIds && filters.brandIds.length > 0) {
    params.brandIds = filters.brandIds.join(',');
  }
  if (filters.customerIds && filters.customerIds.length > 0) {
    params.customerIds = filters.customerIds.join(',');
  }
  if (filters.territoryIds && filters.territoryIds.length > 0) {
    params.territoryIds = filters.territoryIds.join(',');
  }
  if (filters.salesPersonIds && filters.salesPersonIds.length > 0) {
    params.salesPersonIds = filters.salesPersonIds.join(',');
  }
}

class ReportService extends BaseApiService {
  /**
   * Get executive dashboard data
   */
  async getExecutiveDashboard(
    filters: ReportFilters = {},
    sortBy?: {
      category?: 'percent' | 'absolute';
      brand?: 'percent' | 'absolute';
      product?: 'percent' | 'absolute';
      customer?: 'percent' | 'absolute';
      salesAgent?: 'percent' | 'absolute';
      territory?: 'percent' | 'absolute';
    },
    mode?: {
      category?: ItemMode;
      brand?: ItemMode;
      product?: ItemMode;
      customer?: ItemMode;
      salesAgent?: ItemMode;
      territory?: ItemMode;
    },
    limit?: {
      category?: number;
      brand?: number;
      product?: number;
      customer?: number;
      salesAgent?: number;
      territory?: number;
    },
    correlationId?: string
  ): Promise<ExecutiveDashboardData> {
    const params: Record<string, any> = {};

    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.compareMode) params.compareMode = filters.compareMode;
    appendReportFilterIdsToParams(params, filters);

    // Add sortBy parameters
    if (sortBy) {
      if (sortBy.category) params.sortByCategory = sortBy.category;
      if (sortBy.brand) params.sortByBrand = sortBy.brand;
      if (sortBy.product) params.sortByProduct = sortBy.product;
      if (sortBy.customer) params.sortByCustomer = sortBy.customer;
      if (sortBy.salesAgent) params.sortBySalesAgent = sortBy.salesAgent;
      if (sortBy.territory) params.sortByTerritory = sortBy.territory;
    }

    // Add mode parameters
    if (mode) {
      if (mode.category) params.modeCategory = mode.category;
      if (mode.brand) params.modeBrand = mode.brand;
      if (mode.product) params.modeProduct = mode.product;
      if (mode.customer) params.modeCustomer = mode.customer;
      if (mode.salesAgent) params.modeSalesAgent = mode.salesAgent;
      if (mode.territory) params.modeTerritory = mode.territory;
    }

    // Send 0 to skip movers queries; omit only when the field is undefined.
    if (limit) {
      if (limit.category !== undefined) params.limitCategory = limit.category;
      if (limit.brand !== undefined) params.limitBrand = limit.brand;
      if (limit.product !== undefined) params.limitProduct = limit.product;
      if (limit.customer !== undefined) params.limitCustomer = limit.customer;
      if (limit.salesAgent !== undefined) params.limitSalesAgent = limit.salesAgent;
      if (limit.territory !== undefined) params.limitTerritory = limit.territory;
    }

    const response = await this.get<{ success: boolean; data: ExecutiveDashboardData }>(
      '/reports/executive-dashboard',
      params,
      correlationId
    );

    if (response && response.success && response.data) {
      return response.data;
    }
    throw new Error('Failed to fetch executive dashboard data');
  }

  /**
   * Get drill-down data for a specific item
   */
  async getDrillDownData(
    type: 'customer' | 'product' | 'salesAgent' | 'territory' | 'category' | 'brand',
    itemId: string | number,
    filters: ReportFilters = {},
    mode: ItemMode = 'decliners',
    limit?: number
  ): Promise<{ [key: string]: TopDecliner[] }> {
    const params: Record<string, any> = {
      type,
      itemId: String(itemId),
    };

    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.compareMode) params.compareMode = filters.compareMode;
    appendReportFilterIdsToParams(params, filters);

    // Add mode and limit
    params.mode = mode;
    if (limit !== undefined) params.limit = limit;

    const response = await this.get<{ success: boolean; data: { [key: string]: TopDecliner[] } }>(
      '/reports/executive-dashboard/drill-down',
      params
    );

    if (response && response.success && response.data) {
      return response.data;
    }
    throw new Error('Failed to fetch drill-down data');
  }

  /**
   * Export Top Decliners to Excel
   */
  async exportTopDeclinersToExcel(
    type: 'category' | 'brand' | 'product' | 'customer' | 'salesAgent' | 'territory',
    filters: ReportFilters = {},
    drillDownType?: 'customer' | 'product' | 'salesAgent' | 'territory' | 'category' | 'brand',
    drillDownId?: string | number,
    sortBy?: 'percent' | 'absolute',
    mode?: 'performers' | 'decliners'
  ): Promise<Blob> {
    const params: Record<string, any> = {
      type,
    };

    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.compareMode) params.compareMode = filters.compareMode;
    if (drillDownType) params.drillDownType = drillDownType;
    if (drillDownId !== undefined) params.drillDownId = String(drillDownId);
    if (sortBy) params.sortBy = sortBy;
    if (mode) params.mode = mode;
    appendReportFilterIdsToParams(params, filters);

    const token = localStorage.getItem('token');
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/reports/executive-dashboard/export?${queryString}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Export failed' }));
      throw new Error(errorData.message || `Export failed with status: ${response.status}`);
    }

    return await response.blob();
  }

  /**
   * Get year-end sales projection
   */
  async getYearEndProjection(
    year?: number,
    currentRevenue?: number,
    priorRevenue?: number,
    filters?: ReportFilters
  ): Promise<YearEndProjection> {
    const params: Record<string, any> = {};
    if (year) params.year = year;
    if (currentRevenue !== undefined) params.currentRevenue = currentRevenue;
    if (priorRevenue !== undefined) params.priorRevenue = priorRevenue;
    if (filters?.startDate) params.startDate = filters.startDate;
    if (filters?.endDate) params.endDate = filters.endDate;
    if (filters?.compareMode) params.compareMode = filters.compareMode;
    if (filters) appendReportFilterIdsToParams(params, filters);

    const response = await this.get<{ success: boolean; data: YearEndProjection }>(
      '/reports/executive-dashboard/year-end-projection',
      params
    );

    if (response && response.success && response.data) {
      return response.data;
    }
    throw new Error('Failed to fetch year-end projection');
  }

  /**
   * Export KPI detail (Revenue or Orders) to Excel
   */
  async exportExecutiveDashboardKPI(
    metric: 'revenue' | 'orders',
    filters: ReportFilters = {},
    segmentType?: 'customer' | 'product' | 'salesagent' | 'territory' | 'category' | 'brand',
    segmentId?: string | number
  ): Promise<Blob> {
    const params: Record<string, any> = {
      metric,
    };

    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.compareMode) params.compareMode = filters.compareMode;
    if (segmentType) params.segmentType = segmentType;
    if (segmentId !== undefined) params.segmentId = segmentId;
    appendReportFilterIdsToParams(params, filters);

    const token = localStorage.getItem('token');
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/reports/executive-dashboard/kpi-export?${queryString}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Export failed' }));
      throw new Error(errorData.message || `Export failed with status: ${response.status}`);
    }

    return await response.blob();
  }

  /**
   * Get segmented data for a specific item (URL-based navigation)
   */
  /**
   * Get single dimension data (for mode/sort changes without reloading entire dashboard)
   */
  async getDimensionData(
    type: 'category' | 'brand' | 'product' | 'customer' | 'salesAgent' | 'territory',
    filters: ReportFilters = {},
    sortBy: 'percent' | 'absolute' = 'absolute',
    mode: ItemMode = 'decliners',
    limit?: number
  ): Promise<TopDecliner[]> {
    const params: Record<string, any> = {};
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.compareMode) params.compareMode = filters.compareMode;
    appendReportFilterIdsToParams(params, filters);
    params.sortBy = sortBy;
    params.mode = mode;
    if (limit !== undefined) params.limit = limit;

    const urlType = type === 'salesAgent' ? 'salesagent' : type.toLowerCase();
    const response = await this.get<{
      success: boolean;
      data: { type: string; items: TopDecliner[] };
    }>(`/reports/executive-dashboard/dimension/${urlType}`, params);

    if (response && response.success && response.data) {
      return response.data.items;
    }
    throw new Error(`Failed to fetch dimension data for ${type}`);
  }

  async getSegmentedData(
    type: 'customer' | 'product' | 'salesagent' | 'territory' | 'category' | 'brand',
    id: string | number,
    filters: ReportFilters = {},
    mode: ItemMode = 'decliners',
    limit?: number,
    correlationId?: string
  ): Promise<SegmentedDashboardData> {
    const params: Record<string, any> = {};

    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.compareMode) params.compareMode = filters.compareMode;
    appendReportFilterIdsToParams(params, filters);
    params.mode = mode;
    if (limit !== undefined) params.limit = limit;

    const response = await this.get<{ success: boolean; data: SegmentedDashboardData }>(
      `/reports/executive-dashboard/${type}/${id}`,
      params,
      correlationId
    );

    if (response && response.success && response.data) {
      return response.data;
    }
    throw new Error('Failed to fetch segmented data');
  }

  /**
   * Get detailed item data for a secondary segment
   */
  async getDetailedItemData(
    type: 'customer' | 'product' | 'salesagent' | 'territory' | 'category' | 'brand',
    id: string | number,
    segmentType: 'products' | 'customers' | 'salesAgents' | 'brands' | 'territories' | 'categories',
    filters: ReportFilters = {},
    mode: ItemMode = 'decliners',
    correlationId?: string
  ): Promise<DetailedItemData[]> {
    const params: Record<string, any> = {
      segmentType,
      mode,
    };

    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.compareMode) params.compareMode = filters.compareMode;
    appendReportFilterIdsToParams(params, filters);

    const response = await this.get<{ success: boolean; data: DetailedItemData[] }>(
      `/reports/executive-dashboard/${type}/${id}/detailed-items`,
      params,
      correlationId
    );

    if (response && response.success && response.data) {
      return response.data;
    }
    throw new Error('Failed to fetch detailed items data');
  }

  /**
   * Export detailed items data to Excel
   */
  async exportDetailedItemsToExcel(
    type: 'customer' | 'product' | 'salesagent' | 'territory' | 'category' | 'brand',
    id: string | number,
    segmentType: 'products' | 'customers' | 'salesAgents' | 'brands' | 'territories' | 'categories',
    filters: ReportFilters = {},
    mode: ItemMode = 'decliners'
  ): Promise<Blob> {
    const params: Record<string, any> = {
      segmentType,
      mode,
    };

    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.compareMode) params.compareMode = filters.compareMode;
    appendReportFilterIdsToParams(params, filters);

    const token = localStorage.getItem('token');
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/reports/executive-dashboard/${type}/${id}/detailed-items/export?${queryString}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Export failed' }));
      throw new Error(errorData.message || `Export failed with status: ${response.status}`);
    }

    return await response.blob();
  }

  /**
   * Export segmented data (invoice line items) to Excel
   */
  async exportSegmentedData(
    type: 'customer' | 'product' | 'salesagent' | 'territory' | 'category' | 'brand',
    id: string | number,
    filters: ReportFilters = {}
  ): Promise<Blob> {
    const params: Record<string, any> = {};

    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.compareMode) params.compareMode = filters.compareMode;
    appendReportFilterIdsToParams(params, filters);

    const token = localStorage.getItem('token');
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/reports/executive-dashboard/${type}/${id}/export?${queryString}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Export failed' }));
      throw new Error(errorData.message || `Export failed with status: ${response.status}`);
    }

    return await response.blob();
  }

  /**
   * Search categories
   */
  async searchCategories(searchTerm: string): Promise<string[]> {
    const response = await this.get<{ success: boolean; data: string[] }>(
      '/products/unique/categories'
    );
    if (response && response.success && response.data) {
      const allCategories = response.data;
      if (!searchTerm.trim()) return allCategories;
      const lowerSearch = searchTerm.toLowerCase();
      return allCategories.filter((cat) => cat.toLowerCase().includes(lowerSearch));
    }
    return [];
  }

  /**
   * Search brands (from products table, not brands table, to match widget behavior)
   */
  async searchBrandsFromProducts(searchTerm: string): Promise<string[]> {
    const response = await this.get<{ success: boolean; data: string[] }>(
      '/products/unique/brands'
    );
    if (response && response.success && response.data) {
      const allBrands = response.data;
      if (!searchTerm.trim()) return allBrands;
      const lowerSearch = searchTerm.toLowerCase();
      return allBrands.filter((brand) => brand.toLowerCase().includes(lowerSearch));
    }
    return [];
  }

  /**
   * Search customers
   */
  async searchCustomers(searchTerm: string): Promise<Array<{ id: string; name: string }>> {
    // Use the filter-options endpoint to get customers
    const response = await this.get<{
      success: boolean;
      data: { customers?: Array<{ id: string; name: string }> };
    }>('/reports/customer-sales/customer-sales-comparison/filter-options', {
      search: searchTerm,
    });
    if (response && response.success && response.data) {
      return response.data.customers || [];
    }
    return [];
  }

  /**
   * Search sales agents
   */
  async searchSalesAgents(searchTerm: string): Promise<string[]> {
    const response = await this.get<{
      success: boolean;
      data: { salesPersonIds?: string[] };
    }>('/reports/customer-sales/customer-sales-comparison/filter-options');
    if (response && response.success && response.data) {
      const allAgents = response.data.salesPersonIds || [];
      if (!searchTerm.trim()) return allAgents;
      const lowerSearch = searchTerm.toLowerCase();
      return allAgents.filter((agent) => agent.toLowerCase().includes(lowerSearch));
    }
    return [];
  }

  /**
   * Search territories
   */
  async searchTerritories(searchTerm: string): Promise<string[]> {
    const response = await this.get<{
      success: boolean;
      data: { territories?: string[] };
    }>('/reports/customer-sales/customer-sales-comparison/filter-options');
    if (response && response.success && response.data) {
      const allTerritories = response.data.territories || [];
      if (!searchTerm.trim()) return allTerritories;
      const lowerSearch = searchTerm.toLowerCase();
      return allTerritories.filter((terr) => terr.toLowerCase().includes(lowerSearch));
    }
    return [];
  }
}

export const reportService = new ReportService();
